// src/screens/customer/customerPannel/orderdetails/OrderDetails.jsx

import axios from "axios";
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  CreditCard,
  Download,
  Hash,
  Home,
  ImageIcon,
  MapPin,
  MessageCircle,
  Package,
  Phone,
  Send,
  Star,
  ThumbsUp,
  Truck,
  X,
} from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "../../../../assets/css/customer/customerPannel/orderdetails/OrderDetails.css";
import AppContext from "../../../../context/AppContext";

// ─────────────────────────────────────────────────────────────
// CONSTANTS — matched with admin statuses
// ─────────────────────────────────────────────────────────────
const STATUS_META = {
  Pending: { color: "#b45309", bg: "rgba(234,179,8,0.1)", label: "Pending" },
  "In Progress": {
    color: "#2563eb",
    bg: "rgba(37,99,235,0.1)",
    label: "In Progress",
  },
  Completed: {
    color: "#16a34a",
    bg: "rgba(22,163,74,0.1)",
    label: "Completed",
  },
  Canceled: { color: "#dc2626", bg: "rgba(220,38,38,0.1)", label: "Canceled" },
  Delivered: {
    color: "#16a34a",
    bg: "rgba(22,163,74,0.1)",
    label: "Delivered",
  },
  "In Transit": {
    color: "#d97706",
    bg: "rgba(217,119,6,0.1)",
    label: "In Transit",
  },
  Processing: {
    color: "#2563eb",
    bg: "rgba(37,99,235,0.1)",
    label: "Processing",
  },
  Cancelled: {
    color: "#dc2626",
    bg: "rgba(220,38,38,0.1)",
    label: "Cancelled",
  },
};

const TIMELINE_ICONS = [ClipboardCheck, CheckCircle, Package, Truck, Home];
const RATING_LABELS = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];

const ORDER_TYPE_LABEL = {
  normal: "Normal Order",
  custom: "Custom Order",
};

// ─────────────────────────────────────────────────────────────
// HELPER — capitalize first letter of status from API
// ─────────────────────────────────────────────────────────────
const capitalizeStatus = (status) => {
  if (!status) return "Pending";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

// ─────────────────────────────────────────────────────────────
// HELPER — transform raw API order into shape the UI expects
// ─────────────────────────────────────────────────────────────
const transformOrder = (raw, baseUrlForFile, customerName) => {
  if (!raw) return null;

  const items = (raw.adcart || []).map((cartItem) => {
    // ── Materials logic ──────────────────────────────────
    let materials = [];
    if (cartItem.type === "normal") {
      materials = (cartItem.adproduct?.productmaterials || []).map((pm) => ({
        name: pm.admaterial?.name || "—",
        category: pm.admaterial?.adcategory_id
          ? `Category #${pm.admaterial.adcategory_id}`
          : "—",
      }));
    } else if (cartItem.type === "custom") {
      materials = (cartItem.custom_cart_materials || []).map((cm) => ({
        name: cm.admaterial?.name || "—",
        category: cm.admaterial?.adcategory_id
          ? `Category #${cm.admaterial.adcategory_id}`
          : "—",
      }));
    }

    return {
      id: cartItem.id,
      name: cartItem.adproduct?.name || "—",
      image: cartItem.adproduct?.productimages?.[0]?.image_path
        ? `${baseUrlForFile}${cartItem.adproduct.productimages[0].image_path}`
        : "",
      quantity: cartItem.quantity,
      price: cartItem.price,
      color: cartItem.adproduct?.color || "—",
      size: cartItem.adproduct?.size || "—",
      sku: `PRD-${cartItem.adproduct_id}`,
      materials,
      itemType: cartItem.type || "normal",
    };
  });

  // Calculate total from adcart items (since order-level total_price is 0)
  const calculatedTotal = (raw.adcart || []).reduce(
    (sum, c) => sum + (c.total_price || 0),
    0,
  );

  const shippingFee = raw.shippingFee || 0;

  const status = capitalizeStatus(raw.status);

  // Build default timeline based on status
  const buildTimeline = (status, createdAt) => {
    const dateStr = new Date(createdAt).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const steps = [
      { label: "Order Placed", date: dateStr, done: true },
      {
        label: "Confirmed",
        date: status !== "Pending" ? dateStr : "—",
        done: status !== "Pending",
      },
      {
        label: "In Progress",
        date:
          status === "In Progress" || status === "Completed" ? dateStr : "—",
        done: status === "In Progress" || status === "Completed",
      },
      {
        label: "Completed",
        date: status === "Completed" ? dateStr : "—",
        done: status === "Completed",
      },
    ];
    return steps;
  };

  const hasReview = !!(raw.rating && raw.reviews);

  return {
    id: raw.id,
    type: raw.type || "normal",
    date: raw.created_at,
    status,
    estimatedDelivery: null,
    deliveredOn: status === "Completed" ? raw.updated_at : null,
    items,
    shippingAddress: {
      name: customerName || raw.adcustomer?.fullName || "Customer",
      phone: raw.phone || "—",
      address: raw.address || "—",
      city: "—",
      postalCode: "—",
      country: "Pakistan",
    },
    paymentMethod: "Bank Transfer",
    subtotal: calculatedTotal,
    shippingFee: shippingFee,
    discount: 0,
    tax: 0,
    total: calculatedTotal + shippingFee,
    timeline: buildTimeline(status, raw.created_at),
    customRequest: raw.customRequest || null,
    hasReview,
    reviewRating: Number(raw.rating) || 0,
    reviewText: raw.reviews || "",
  };
};

// ─────────────────────────────────────────────────────────────
// INVOICE GENERATOR
// ─────────────────────────────────────────────────────────────
const generateInvoice = (order) => {
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Invoice — ${order.id}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Georgia,serif;color:#2c2c2c;background:#fff;padding:48px}
    .inv-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;padding-bottom:24px;border-bottom:2px solid #f0e6d8}
    .inv-brand{font-size:1.8rem;font-weight:800;color:#D67E43}
    .inv-brand small{display:block;font-size:.75rem;color:#9a7060;font-weight:400;margin-top:2px}
    .inv-meta{text-align:right}.inv-meta h2{font-size:1.4rem;font-weight:700}
    .inv-meta p{font-size:.82rem;color:#6B4C3B;margin-top:4px}
    .inv-id{font-family:monospace;color:#D67E43;font-weight:700}
    .inv-parties{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-bottom:32px}
    .inv-party h3{font-size:.68rem;text-transform:uppercase;letter-spacing:1.5px;color:#9a7060;margin-bottom:10px}
    .inv-party p{font-size:.86rem;color:#6B4C3B;line-height:1.7}
    .inv-party .name{font-size:1rem;font-weight:700;color:#2c2c2c}
    table{width:100%;border-collapse:collapse;margin-bottom:28px}
    thead tr{background:#5C6B47}thead th{padding:11px 14px;text-align:left;color:#fff;font-size:.72rem;text-transform:uppercase;letter-spacing:.5px}
    tbody tr{border-bottom:1px solid #faf3eb}tbody td{padding:13px 14px;font-size:.86rem;color:#6B4C3B}
    .td-name{font-weight:600;color:#2c2c2c}.td-price{font-weight:700;color:#5C6B47;text-align:right}
    .inv-totals{margin-left:auto;width:280px}
    .inv-totals-row{display:flex;justify-content:space-between;font-size:.86rem;color:#6B4C3B;padding:6px 0}
    .inv-totals-row.total{border-top:2px solid #f0e6d8;margin-top:8px;padding-top:12px;font-size:1rem;font-weight:800;color:#5C6B47}
    .inv-footer{margin-top:48px;padding-top:20px;border-top:1px solid #f0e6d8;text-align:center;font-size:.78rem;color:#9a7060;line-height:1.7}
    .inv-footer strong{color:#D67E43}
    .inv-stamp{display:inline-block;border:2px solid #16a34a;color:#16a34a;font-size:1.2rem;font-weight:800;padding:6px 20px;border-radius:6px;transform:rotate(-8deg);margin-top:16px;letter-spacing:2px}
  </style>
</head>
<body>
  <div class="inv-header">
    <div>
      <div class="inv-brand">AD Furniture House <small>Premium Furniture & Home Décor</small></div>
    </div>
    <div class="inv-meta">
      <h2>INVOICE</h2>
      <p class="inv-id">${order.id}</p>
      <p>Date: ${new Date(order.date).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</p>
    </div>
  </div>
  <div class="inv-parties">
    <div class="inv-party">
      <h3>Bill To</h3>
      <p class="name">${order.shippingAddress.name}</p>
      <p>${order.shippingAddress.address}<br/>${order.shippingAddress.country}<br/>${order.shippingAddress.phone}</p>
    </div>
    <div class="inv-party">
      <h3>Payment Info</h3>
      <p><strong>Method:</strong> ${order.paymentMethod}</p>
      <p><strong>Status:</strong> ${order.status === "Completed" ? "Paid" : "Pending"}</p>
    </div>
  </div>
  <table>
    <thead><tr><th>#</th><th>Item</th><th>Color / Size</th><th>Qty</th><th style="text-align:right">Price</th><th style="text-align:right">Total</th></tr></thead>
    <tbody>
      ${order.items.map((it, i) => `<tr><td>${i + 1}</td><td class="td-name">${it.name}</td><td>${it.color} / ${it.size}</td><td>${it.quantity}</td><td class="td-price">PKR ${it.price.toLocaleString()}</td><td class="td-price">PKR ${(it.price * it.quantity).toLocaleString()}</td></tr>`).join("")}
    </tbody>
  </table>
  <div class="inv-totals">
    <div class="inv-totals-row"><span>Subtotal</span><span>PKR ${order.subtotal.toLocaleString()}</span></div>
    <div class="inv-totals-row"><span>Shipping Fee</span><span>PKR ${order.shippingFee.toLocaleString()}</span></div>
    ${order.discount > 0 ? `<div class="inv-totals-row"><span>Discount</span><span>- PKR ${order.discount.toLocaleString()}</span></div>` : ""}
    <div class="inv-totals-row total"><span>Grand Total</span><span>PKR ${order.total.toLocaleString()}</span></div>
  </div>
  <div class="inv-footer">
    <p>Thank you for shopping with <strong>AD Furniture House</strong>.</p>
    ${order.status === "Completed" ? '<div class="inv-stamp">PAID</div>' : ""}
  </div>
</body>
</html>`;
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Invoice-${order.id}.html`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────
// STAR RATING
// ─────────────────────────────────────────────────────────────
const StarRating = ({ value, onChange, readonly = false }) => (
  <div className="rv-stars" aria-label={`Rating: ${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        className={`rv-star ${n <= value ? "rv-star--filled" : ""} ${readonly ? "rv-star--readonly" : ""}`}
        onClick={() => !readonly && onChange(n)}
        aria-label={`${n} star`}
      >
        <Star size={readonly ? 14 : 26} strokeWidth={1.8} />
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
// REVIEW POPUP
// ─────────────────────────────────────────────────────────────
const ReviewPopup = ({ order, onClose, onSubmit }) => {
  const { baseUrl, authToken } = useContext(AppContext);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [reviews, setReviews] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (rating === 0) {
      setError("Please select a star rating.");
      return;
    }
    if (reviews.trim().length < 10) {
      setError("Write at least 10 characters.");
      return;
    }
    setError("");

    const formData = new FormData();
    formData.append("id", order.id);
    formData.append("ratings", rating);
    formData.append("reviews", reviews);
    axios
      .post(`${baseUrl}/customer/order/updateReview`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        setSubmitted(true);
        onSubmit();
      })
      .catch((error) => {
        console.log(error);
        setError("Something went wrong. Please try again.");
      });
  };

  return (
    <div
      className="rv-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="rv-popup">
        <div className="rv-popup-head">
          <div className="rv-popup-headleft">
            <div className="rv-popup-icon">
              <Star size={16} strokeWidth={2} />
            </div>
            <div>
              <h3 className="rv-popup-title">Write a Review</h3>
              <p className="rv-popup-sub">
                Share your experience with this order
              </p>
            </div>
          </div>
          <button className="rv-close" onClick={onClose}>
            <X size={17} strokeWidth={2.5} />
          </button>
        </div>

        {!submitted ? (
          <>
            <div className="rv-order-preview">
              <img
                src={order.items[0].image}
                alt={order.items[0].name}
                className="rv-preview-img"
              />
              <div>
                <p className="rv-preview-name">
                  {order.items[0].name}
                  {order.items.length > 1
                    ? ` + ${order.items.length - 1} more`
                    : ""}
                </p>
                <p className="rv-preview-id">Order {order.id}</p>
              </div>
            </div>
            <div className="rv-section">
              <label className="rv-label">Overall Rating</label>
              <div className="rv-stars-row">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`rv-star ${n <= (hovered || rating) ? "rv-star--filled" : ""}`}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHovered(n)}
                    onMouseLeave={() => setHovered(0)}
                  >
                    <Star size={30} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
              <p className="rv-rating-label rv-rating-label--reserved">
                {RATING_LABELS[hovered || rating] || "\u00A0"}
              </p>
            </div>
            <div className="rv-section">
              <label className="rv-label" htmlFor="rv-comment">
                Your Review
              </label>
              <textarea
                id="rv-comment"
                className="rv-textarea"
                rows={4}
                placeholder="Tell us what you loved (or didn't)..."
                value={reviews}
                onChange={(e) => {
                  setReviews(e.target.value);
                  setError("");
                }}
                maxLength={500}
              />
              <div className="rv-char-count">{reviews.length}/500</div>
            </div>
            {error && (
              <p className="rv-error">
                <AlertCircle size={13} strokeWidth={2} />
                {error}
              </p>
            )}
            <div className="rv-popup-actions">
              <button className="rv-btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button className="rv-btn-submit" onClick={handleSubmit}>
                <Send size={14} strokeWidth={2.2} />
                Submit Review
              </button>
            </div>
          </>
        ) : (
          <div className="rv-success">
            <div className="rv-success-icon">
              <ThumbsUp size={28} strokeWidth={1.8} />
            </div>
            <h4 className="rv-success-title">Review Submitted!</h4>
            <p className="rv-success-msg">
              Thank you for your feedback. It helps us serve you better.
            </p>
            <button
              className="rv-btn-submit"
              onClick={onClose}
              style={{ marginTop: "20px", width: "100%" }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// CUSTOM ORDER DETAILS CARD  (type: custom)
// ─────────────────────────────────────────────────────────────
const CustomRequestCard = ({ data }) => (
  <div className="od-card od-custom-card od-custom-card--request">
    <div className="od-card-head">
      <h2 className="od-card-title">
        <ImageIcon size={15} strokeWidth={2.2} /> Custom Order Details
      </h2>
      <span
        className="od-card-chip"
        style={{ background: "rgba(92,107,71,0.1)", color: "#5c6b47" }}
      >
        Custom Order
      </span>
    </div>
    <div className="od-custom-grid">
      <div className="od-custom-item">
        <Package size={13} className="od-custom-icon" />
        <div>
          <p className="od-custom-label">Furniture Type</p>
          <span className="od-custom-badge od-custom-badge--wood">
            {data.furnitureType}
          </span>
        </div>
      </div>
      <div className="od-custom-item">
        <CreditCard size={13} className="od-custom-icon" />
        <div>
          <p className="od-custom-label">Quoted Price</p>
          <p className="od-cr-quoted">
            PKR {data.quotedPrice?.toLocaleString()}
          </p>
        </div>
      </div>
      <div className="od-custom-item">
        <Calendar size={13} className="od-custom-icon" />
        <div>
          <p className="od-custom-label">Est. Delivery</p>
          <p className="od-custom-value">{data.estimatedDays} days</p>
        </div>
      </div>
      <div className="od-custom-item">
        <ImageIcon size={13} className="od-custom-icon" />
        <div>
          <p className="od-custom-label">Reference Images</p>
          <p className="od-custom-value">
            {data.referenceImages?.length} uploaded
          </p>
        </div>
      </div>
    </div>
    <div className="od-cr-description">
      <p className="od-custom-label" style={{ marginBottom: "0.4rem" }}>
        Your Description
      </p>
      <p className="od-cr-desc-text">"{data.description}"</p>
    </div>
    {data.referenceImages?.length > 0 && (
      <div className="od-cr-images-row">
        {data.referenceImages.map((src, i) => (
          <a
            key={i}
            href={src}
            target="_blank"
            rel="noreferrer"
            className="od-cr-img-wrap"
          >
            <img src={src} alt={`ref-${i}`} className="od-cr-img" />
            <div className="od-cr-img-overlay">View</div>
          </a>
        ))}
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { authToken, baseUrl, baseUrlForFile, loggedInCustomer } =
    useContext(AppContext);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [order, setOrder] = useState(null);

  const orderDetails = () => {
    const formData = new FormData();
    formData.append("adcustomer_id", loggedInCustomer?.id);
    formData.append("id", id);
    axios
      .post(`${baseUrl}/customer/order/viewOrderDetails`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        const raw = response.data.orderItem?.[0];
        setOrder(
          transformOrder(raw, baseUrlForFile, loggedInCustomer?.fullName),
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    orderDetails();
  }, []);

  if (!order) {
    return (
      <div
        className="od-page"
        style={{ textAlign: "center", padding: "60px 20px" }}
      >
        <h2 style={{ color: "#D67E43" }}>Order Not Found</h2>
        <p style={{ color: "#9a7060", marginTop: "8px" }}>
          Order ID: <strong>{id}</strong> does not exist.
        </p>
        <Link
          to="/customer/orders"
          style={{
            color: "#D67E43",
            marginTop: "20px",
            display: "inline-block",
          }}
        >
          ← Back to Orders
        </Link>
      </div>
    );
  }

  const sm = STATUS_META[order.status] || STATUS_META["Pending"];
  const visibleSteps = showAllSteps ? order.timeline : order.timeline.slice(-3);
  const isCompleted =
    order.status === "Completed" || order.status === "Delivered";
  const isCanceled =
    order.status === "Canceled" || order.status === "Cancelled";

  return (
    <div className="od-page">
      {/* ══ TOP BAR ══ */}
      <div className="od-topbar">
        <Link to="/customer/orders" className="od-back">
          <ArrowLeft size={15} strokeWidth={2.5} /> Back to Orders
        </Link>
        <div className="od-topbar-meta">
          <span className="od-meta-id">
            <Hash size={12} strokeWidth={2.5} />
            {order.id}
          </span>
          <span className="od-meta-date">
            <Calendar size={12} strokeWidth={2.5} />
            {new Date(order.date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
          {order.type !== "normal" && (
            <span className="od-type-pill">
              <ImageIcon size={10} />
              {ORDER_TYPE_LABEL[order.type]}
            </span>
          )}
          <span
            className="od-status-pill"
            style={{ color: sm.color, background: sm.bg }}
          >
            {sm.label}
          </span>
        </div>
      </div>

      {/* ══ MAIN GRID ══ */}
      <div className="od-grid">
        {/* ━━ LEFT ━━ */}
        <div className="od-col-left">
          {/* Order Items */}
          <div className="od-card">
            <div className="od-card-head">
              <h2 className="od-card-title">
                <Package size={15} strokeWidth={2.2} /> Order Items
              </h2>
              <span className="od-card-chip">
                {order.items.length} item{order.items.length > 1 ? "s" : ""}
              </span>
            </div>
            <div className="od-items">
              {order.items.map((item) => (
                <div key={item.id} className="od-item">
                  <div className="od-item-img-wrap">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="od-item-img"
                    />
                  </div>
                  <div className="od-item-info">
                    <p className="od-item-name">{item.name}</p>
                    <p className="od-item-specs">
                      {item.color} · {item.size}
                    </p>
                    <p className="od-item-sku">SKU: {item.sku}</p>
                    <div className="od-item-footer">
                      <span className="od-item-qty">Qty: {item.quantity}</span>
                      <span className="od-item-price">
                        PKR {item.price.toLocaleString()}
                      </span>
                    </div>

                    {/* ── Materials Table ── */}
                    {item.materials?.length > 0 && (
                      <div className="od-materials-wrap">
                        <p className="od-materials-heading">
                          {item.itemType === "custom"
                            ? "Custom Materials"
                            : "Product Materials"}
                        </p>
                        <table className="od-materials-table">
                          <thead>
                            <tr>
                              <th>Material</th>
                              <th>Category</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.materials.map((mat, idx) => (
                              <tr key={idx}>
                                <td>{mat.name}</td>
                                <td>{mat.category}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {isCompleted && !order.hasReview && (
              <div className="od-item-actions">
                <button
                  className="od-item-action"
                  onClick={() => setReviewOpen(true)}
                >
                  <Star size={13} strokeWidth={2} /> Write a Review
                </button>
              </div>
            )}
          </div>

          {order.hasReview && (
            <div className="od-card od-reviews-card">
              <div className="od-card-head">
                <h2 className="od-card-title">
                  <Star size={15} strokeWidth={2.2} /> Your Review
                </h2>
              </div>
              <div className="od-reviews-list">
                <div className="od-review-item">
                  <div className="od-review-header">
                    <div className="od-review-avatar">
                      {order.shippingAddress.name.charAt(0)}
                    </div>
                    <div className="od-review-meta">
                      <p className="od-review-author">
                        {order.shippingAddress.name}
                      </p>
                      <p className="od-review-date">Verified Purchase</p>
                    </div>
                    <div className="od-review-badge">
                      <CheckCircle size={11} strokeWidth={2.5} />
                      Verified
                    </div>
                  </div>
                  <div className="od-review-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        size={14}
                        strokeWidth={1.8}
                        className={
                          n <= order.reviewRating
                            ? "od-star-filled"
                            : "od-star-empty"
                        }
                      />
                    ))}
                    <span className="od-review-rating-label">
                      {RATING_LABELS[order.reviewRating]}
                    </span>
                  </div>
                  <p className="od-review-comment">"{order.reviewText}"</p>
                </div>
              </div>
            </div>
          )}

          {order.type === "custom" && order.customRequest && (
            <CustomRequestCard data={order.customRequest} />
          )}

          {/* Timeline */}
          <div className="od-card">
            <div className="od-card-head">
              <h2 className="od-card-title">
                <Truck size={15} strokeWidth={2.2} /> Order Tracking
              </h2>
              {isCompleted && (
                <span className="od-delivered-tag">✓ Completed</span>
              )}
            </div>
            <div className="od-timeline">
              {visibleSteps.map((step, i) => {
                const Icon =
                  TIMELINE_ICONS[order.timeline.indexOf(step)] || CheckCircle;
                const isLast = i === visibleSteps.length - 1;
                return (
                  <div
                    key={i}
                    className={`od-step ${step.done ? "od-step-done" : ""}`}
                  >
                    <div className="od-step-left">
                      <div className="od-step-dot">
                        <Icon size={12} strokeWidth={2.5} />
                      </div>
                      {!isLast && <div className="od-step-line" />}
                    </div>
                    <div className="od-step-body">
                      <p className="od-step-label">{step.label}</p>
                      <p className="od-step-date">{step.date}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {order.timeline.length > 3 && (
              <button
                className="od-toggle-steps"
                onClick={() => setShowAllSteps((p) => !p)}
              >
                {showAllSteps ? (
                  <>
                    <ChevronUp size={13} strokeWidth={2.5} /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDown size={13} strokeWidth={2.5} /> Show all{" "}
                    {order.timeline.length} steps
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* ━━ RIGHT ━━ */}
        <div className="od-col-right">
          {/* Delivery Info */}
          <div className="od-card">
            <div className="od-card-head">
              <h2 className="od-card-title">
                <MapPin size={15} strokeWidth={2.2} /> Delivery Info
              </h2>
            </div>
            <div className="od-address">
              <p className="od-address-name">{order.shippingAddress.name}</p>
              <p className="od-address-line">
                <Phone size={12} strokeWidth={2} />
                {order.shippingAddress.phone}
              </p>
              <p className="od-address-block">
                {order.shippingAddress.address},<br />
                {order.shippingAddress.country}
              </p>
              {order.deliveredOn && (
                <div className="od-delivery-confirmed">
                  <CheckCircle size={13} strokeWidth={2.5} /> Completed on{" "}
                  {new Date(order.deliveredOn).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              )}
              {order.estimatedDelivery && !isCompleted && !isCanceled && (
                <div className="od-delivery-eta">
                  <Calendar size={12} strokeWidth={2} />
                  Est. delivery:{" "}
                  {new Date(order.estimatedDelivery).toLocaleDateString(
                    "en-GB",
                    { day: "2-digit", month: "short", year: "numeric" },
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bill Summary */}
          <div className="od-card">
            <div className="od-card-head">
              <h2 className="od-card-title">
                <CreditCard size={15} strokeWidth={2.2} /> Bill Summary
              </h2>
            </div>
            <div className="od-summary">
              <div className="od-sum-row">
                <span>Subtotal</span>
                <span>PKR {order.subtotal.toLocaleString()}</span>
              </div>
              <div className="od-sum-row">
                <span>Shipping Fee</span>
                <span>PKR {order.shippingFee.toLocaleString()}</span>
              </div>
              {order.discount > 0 && (
                <div className="od-sum-row od-sum-discount">
                  <span>Discount</span>
                  <span>− PKR {order.discount.toLocaleString()}</span>
                </div>
              )}
              <div className="od-sum-divider" />
              <div className="od-sum-row od-sum-total">
                <span>Total</span>
                <span>PKR {order.total.toLocaleString()}</span>
              </div>
              <div className="od-pay-method">
                <span className="od-pay-label">via</span>
                <span className="od-pay-value">{order.paymentMethod}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="od-actions-card">
            <button
              className="od-btn-primary"
              onClick={() => generateInvoice(order)}
            >
              <Download size={15} strokeWidth={2.2} /> Download Invoice
            </button>
            <button
              className="od-btn-secondary"
              onClick={() => navigate("/customer/help-support")}
            >
              <MessageCircle size={15} strokeWidth={2.2} /> Help & Support
            </button>
            <p className="od-help-hint">
              <AlertCircle size={11} strokeWidth={2} /> Invoice downloads as
              HTML — open in browser and print as PDF
            </p>
          </div>
        </div>
      </div>

      {/* Review Popup */}
      {reviewOpen && (
        <ReviewPopup
          order={order}
          onClose={() => setReviewOpen(false)}
          onSubmit={() => orderDetails()}
        />
      )}
    </div>
  );
};

export default OrderDetails;
